import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  FileQuestionIcons,
  LayoutGridIcons,
  TableIcons,
} from '../../../../../../icons/icons';
import {
  Button,
  DataTable,
  FilterSearch,
  MainLayout,
} from '../../../../../../ui';
import { ResultsGridView } from './Components';
import { GenerateReport } from './Containers/GenerateReport';
import UserDetails from './Containers/UserDetails';
import { getFilterLabels, getGridColumns, getTableColumns } from './Functions';
import { IInterviewResult, useResults } from './Hooks';

interface IResultsProps {
  interviewRoundId: string;
}

export const Results: React.FC<IResultsProps> = ({ interviewRoundId }) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const {
    results,
    pageNumber,
    totalPages,
    rowsPerPage,
    onPageChange,
    handleRowClick,
    handleSelectRow,
    handleSelectAll,
    isOpenUserDetails,
    setIsOpenUserDetails,
    selectedResult,
    sortConfig,
    handleSortChange,
    totalRecords,
    isLoading,
    isRowSelected,
    isAllSelected,
    isIndeterminate,
    selectedCandidateIds,
  } = useResults({ interviewRoundId });

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [isReportGenerate, setIsReportGenerate] = useState<boolean>(false);

  const listColumns = getTableColumns({ t });
  const gridColiumns = getGridColumns({ t });
  const legendItems = [
    { label: t(SI.results.legend.excellent), color: 'bg-green-500' },
    { label: t(SI.results.legend.good), color: 'bg-blue-500' },
    { label: t(SI.results.legend.average), color: 'bg-yellow-500' },
    { label: t(SI.results.legend.notSuitable), color: 'bg-red-500' },
  ];

  const disabled = selectedCandidateIds.length === 0;

  return (
    <>
      <MainLayout>
        <div className='wrapper flex min-w-0 flex-col gap-3 overflow-hidden pr-0'>
          <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
            <div className='min-w-0 flex-1'>
              <FilterSearch
                  filterOptions={getFilterLabels()}
                  onSearch={() => {}}
                  onApplyFilters={() => {}}
              />
            </div>

            <div className='flex flex-wrap items-center gap-2 lg:justify-end'>
              { /* legend */ }
              <div className='relative group'>
                <button
                    type='button'
                    className='flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white transition hover:bg-slate-50'
                >
                  <FileQuestionIcons className='h-4 w-4 text-slate-600' />
                </button>

                <div
                    className='absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg p-3 z-50 shadow-lg
                              opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all'
                >
                  <div className='space-y-2'>
                    { legendItems.map(({ label, color }) => (
                      <div key={label} className='flex items-center space-x-2'>
                        <span className={`w-3 h-3 ${color} rounded-full`} />
                        <span className='text-xs text-gray-700'>{ label }</span>
                      </div>
                    )) }
                  </div>
                </div>
              </div>

              { /* view toggles */ }
              <div className='flex flex-wrap gap-2'>
                <Button
                    type='button'
                    variant={'primary'}
                    onClick={() => {
                      setIsReportGenerate(true);
                    }}
                    label={t(SI.results.generateReport)}
                    disabled={disabled}
                />
                <Button
                    type='button'
                    variant={viewMode === 'table' ? 'primary' : 'secondary'}
                    onClick={() => setViewMode('table')}
                    icon={<TableIcons className='w-5 h-5' />}
                    hasIcon
                    label=''
                />
                <Button
                    type='button'
                    variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                    onClick={() => setViewMode('grid')}
                    icon={<LayoutGridIcons className='w-5 h-5' />}
                    hasIcon
                    label=''
                />
                <GenerateReport
                    isOpen={isReportGenerate}
                    userIds={selectedCandidateIds}
                    interviewRoundId={interviewRoundId}
                    onCloseDrawer={() => setIsReportGenerate(false)}
                    loading={loading}
                    setLoading={setLoading}
                />
              </div>
            </div>
          </div>

          { viewMode === 'table' ? (
            <DataTable<IInterviewResult>
                onClickRow={handleRowClick}
                onSortChange={handleSortChange}
                sortConfig={sortConfig}
                hasCheckBox
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                isRowSelected={isRowSelected}
                isAllSelected={isAllSelected}
                isIndeterminate={isIndeterminate}
                columns={listColumns}
                data={results}
                rowsPerPage={10}
                currentPage={pageNumber}
                totalPages={totalPages}
                onPageChange={onPageChange}
                totalRecords={totalRecords}
                isLoading={isLoading}
            />
          ) : (
            <ResultsGridView<IInterviewResult>
                onSortChange={handleSortChange}
                sortConfig={sortConfig}
                rowHeaders={gridColiumns} // now an array of ColumnDef, not just the labels
                data={results}
                columns={gridColiumns}
                onItemClick={handleRowClick}
                page={pageNumber}
                totalPages={totalPages}
                onPageChange={onPageChange}
                rowsPerPage={rowsPerPage}
                currentPage={pageNumber}
                totalRecords={totalRecords}
            />
          ) }
        </div>
      </MainLayout>

      <UserDetails
          isOpen={isOpenUserDetails}
          onCloseDrawer={() => setIsOpenUserDetails(false)}
          result={selectedResult}
      />
    </>
  );
};
